import * as React from 'react'
import { SelfServiceContainer } from '@/SelfServiceContainer'
import { GeneralModule } from '@/modules/GeneralModule'
import { ReturnsAndExchangesModule } from '@/modules/ReturnsAndExchangesModule'
import { WhatsappModule } from '@/modules/WhatsappModule'
import { FAQModule } from '@/modules/FAQModule'
import { ChatModule } from '@/modules/ChatModule'
import { OrdersModule } from '@/modules/OrdersModule'
import { ScheduleModule } from '@/modules/ScheduleModule'
import { FAQModuleProps } from '@/modules/FAQModule/types'
import { WhatsappModuleProps } from '@/modules/WhatsappModule/types'
import { ReturnsAndExchangesModuleProps } from '@/modules/ReturnsAndExchangesModule/types'
import { useModuleData } from '@/hooks/useModuleData'
import { useContainerUI } from '@/hooks/useContainerUI'
import { ModuleContext } from '../hooks/ModuleContext'
import { Module, MODULE_TITLES } from '@/constants/modules'

interface ModuleControllerProps {
  apiUrl: string
  userId: string
  blipChatAppKey: string
}

const ModuleController: React.FC<ModuleControllerProps> = ({ apiUrl, userId, blipChatAppKey }) => {
  const { module, setModule } = React.useContext(ModuleContext)
  const { setContainerIsOpen, setShowContent, setWhatsappIsOff, setGeneralModule, setTitle } = useContainerUI()
  const moduleDataState = useModuleData(apiUrl, userId, module)

  const { baseData, moduleData, whatsappIsOff, isLogged, ordersError, ordersLoaded, allOrders, schedulesError, schedulesLoaded, allSchedules } = moduleDataState

  const getCurrentModuleTitle = (): string => {
    if (!baseData) return MODULE_TITLES.general

    const foundModule = baseData.find((content) => content.id === module)
    return foundModule?.title ?? MODULE_TITLES.general
  }

  const createModuleContent = (): Record<Module, React.JSX.Element | null> => ({
    'all': baseData ? <GeneralModule sections={baseData} /> : null,
    'orders': <OrdersModule isLogged={isLogged} orders={allOrders} loaded={ordersLoaded} error={ordersError} />,
    'schedules': <ScheduleModule isLogged={isLogged} schedules={allSchedules} error={schedulesError} loaded={schedulesLoaded} />,
    'returns-exchanges': moduleData ? <ReturnsAndExchangesModule {...(moduleData as ReturnsAndExchangesModuleProps)} /> : null,
    'whatsapp': moduleData ? <WhatsappModule {...(moduleData as WhatsappModuleProps)} /> : null,
    'faq': moduleData ? <FAQModule {...(moduleData as FAQModuleProps)} /> : null,
    'chat': <ChatModule blipChatAppKey={blipChatAppKey} />,
  })

  React.useEffect(() => {
    setWhatsappIsOff(whatsappIsOff)
    setGeneralModule(module === 'all')
    const title = module === 'all' ? MODULE_TITLES.general : getCurrentModuleTitle()
    setTitle(title)

    const openToChat = () => {
      setContainerIsOpen(true)
      setModule('chat')
      setTimeout(() => {
        setShowContent(true)
      }, 400)
    }

    window.SelfServiceBallon.openToChat = openToChat

    return () => {
      window.SelfServiceBallon.openToChat = null
    }
  }, [whatsappIsOff, module, baseData, setWhatsappIsOff, setGeneralModule, setTitle, getCurrentModuleTitle])

  if (!baseData) return null

  const moduleContent = createModuleContent()

  return <SelfServiceContainer>{moduleContent[module] || moduleContent.all}</SelfServiceContainer>
}

export { ModuleController }
